import SvgIcon from '@liquidweb-dev/undercurrent/SvgIcon';
import Typography, {
	type TypographyProps,
} from '@liquidweb-dev/undercurrent/Typography';
import { unstable_ClassNameGenerator as ClassNameGenerator } from '@liquidweb-dev/undercurrent/className';
import type { SxProps, Theme } from '@liquidweb-dev/undercurrent/styles';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { ArrowUpRight as ArrowUpRightIcon } from 'lucide-react';
import { type AnchorHTMLAttributes, type ReactNode } from 'react';

const endIconClassname = ClassNameGenerator.generate(
	'LWMuiNavFooterLink-endIcon',
);

const baseHoverStyles: SxProps<Theme> = {
	color: (theme) => theme.vars.palette.uc.text.main,
	outline: 'none',
};

const baseStyles: SxProps<Theme> = {
	height: (theme) => theme.size.sm,
	overflow: 'hidden',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: (theme) => theme.vars.palette.uc.text.medium,
	textDecoration: 'none',
	paddingX: 150,
	paddingY: 50,
	fontWeight: 500,
	borderRadius: (theme) => theme.borderRadius.roundedSm,
	gap: 100,
	[`&:not(:hover):not(:active) > .${endIconClassname}`]: {
		color: 'transparent',
	},
	':hover': baseHoverStyles,
	':active': baseHoverStyles,
	':focus-visible': {
		outlineOffset: '2px',
		outlineWidth: '2px',
		outlineStyle: 'solid',
		outlineColor: (theme) => theme.vars.palette.uc.highlight.border.main,
	},
};

export interface NavFooterLinkProps extends TypographyProps {
	active?: boolean;
	href: AnchorHTMLAttributes<HTMLAnchorElement>['href'];
	target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
	startIcon?: ReactNode;
}

export function NavFooterLink(props: NavFooterLinkProps) {
	const {
		component = 'a',
		sx: sxProp,
		children,
		startIcon,
		...anchorProps
	} = props;

	return (
		<Typography
			component={component}
			sx={combineSx(baseStyles, sxProp)}
			{...anchorProps}
		>
			{startIcon && (
				<SvgIcon sx={combineSx({ height: '16px', width: '16px' })}>
					{startIcon}
				</SvgIcon>
			)}
			<Typography
				component="span"
				variant="label-sm"
				display="block"
				flex="0% 1 1"
				whiteSpace="nowrap"
			>
				{children}
			</Typography>
			<SvgIcon
				sx={{ height: '16px', width: '16px' }}
				className={endIconClassname}
			>
				<ArrowUpRightIcon />
			</SvgIcon>
		</Typography>
	);
}
export default NavFooterLink;
